import * as React from 'react';
import Close from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Drawer,
  ListItem,
  ListItemButton,
  ListItemText,
  styled,
  Typography,
  Menu,
  MenuItem,
  Collapse,
  ListItemIcon,
} from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useRouter } from 'next/router';
import axios from 'axios';
import theme from 'src/themes';
import clearClientStorage from 'src/utilities/storage/clearClientStorage';
import {
  INavigationItemConfiguration,
  INavigationItemChild,
} from 'src/interfaces/microsite/microsite-config.interface';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LanguageSelectorMenu from '../LanguageSelectorMenu';

type MobileNavigationProps = {
  handleDrawerToggle: () => void;
  shouldShowNavLinks: boolean;
  siteNavigation?: INavigationItemConfiguration[];
  shouldShowUserMenu?: boolean;
  shouldShowLoginLink: boolean;
  isMobileDrawerOpen: boolean;
};

type DesktopNavigationProps = {
  siteNavigation?: INavigationItemConfiguration[];
};

type DesktopNavigationChildProps = {
  navigation: INavigationItemConfiguration;
};

type MobileNavigationChildProps = {
  navigation: INavigationItemConfiguration;
  handleDrawerToggle: () => void;
};

const HeaderLinkDesktop = styled(Typography)(() => ({
  color: theme.palette.common.black,
}));

const getLinkHref = (item: INavigationItemConfiguration): string => {
  if (!item?.urlSlug) return '/';
  return item.type === 'ExternalLink' ? item.urlSlug : `/${item.urlSlug}`;
};

const SubmenuItemsMobile = ({
  navigation,
  handleDrawerToggle,
}: MobileNavigationChildProps) => {
  const [isSectionCollapsed, setIsSectionCollapsed] = React.useState(false);
  const handleCollapseToggle = () => {
    setIsSectionCollapsed(!isSectionCollapsed);
  };

  const submenuItemsSorted =
    navigation.children?.sort(function (a, b) {
      return a.order - b.order;
    }) ?? [];

  return (
    <>
      <ListItem disablePadding data-testid="menu-parent-item-mobile">
        <ListItemButton onClick={handleCollapseToggle}>
          <ListItemText
            primary={navigation.name}
            sx={{
              color: isSectionCollapsed
                ? theme.palette.primary.main
                : theme.palette.common.black,
            }}
          />
          <ListItemIcon
            sx={{
              color: isSectionCollapsed
                ? theme.palette.primary.main
                : theme.palette.common.black,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            {isSectionCollapsed ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </ListItemIcon>
        </ListItemButton>
      </ListItem>
      <Collapse in={isSectionCollapsed} timeout="auto" unmountOnExit>
        {submenuItemsSorted.map((item: INavigationItemChild) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton
              component={Link}
              href={getLinkHref(item)}
              onClick={() => handleDrawerToggle()}
            >
              <ListItemText
                primary={item.name}
                sx={{ paddingLeft: theme.spacing(2) }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </Collapse>
    </>
  );
};

const SubmenuItemsDesktop = ({ navigation }: DesktopNavigationChildProps) => {
  const router = useRouter();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const submenuItemsSorted =
    navigation.children?.sort(function (a, b) {
      return a.order - b.order;
    }) ?? [];

  const navigate = (url: string) => {
    router.push(url);
    handleClose();
  };

  const handleItemClick = (item: INavigationItemChild) => {
    if (item.type === 'ExternalLink') {
      window.open(item.urlSlug, '_blank');
    } else {
      navigate(`/${item.urlSlug}`);
    }
    handleClose();
  };

  return (
    <>
      <Button
        id="menu-parent-item"
        aria-controls={open ? 'menu-children-items' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        disableElevation
        disableRipple
        data-testid="menu-parent-item-desktop"
        sx={{
          padding: 0,
          color: open ? theme.palette.primary.main : theme.palette.common.black,
          fontWeight: 400,
          '&:hover': {
            background: theme.palette.common.white,
            padding: 0,
          },
          '&:focus-visible': {
            outline: 'auto',
          },
        }}
      >
        {navigation.name}
      </Button>
      <Menu
        id="menu-children-items"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        data-testid="menu-children-items"
        MenuListProps={{
          'aria-labelledby': 'menu-parent-item',
        }}
        anchorOrigin={{
          vertical: 35,
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {submenuItemsSorted.map((item: INavigationItemChild) => (
          <MenuItem
            sx={{
              minWidth: 200,
            }}
            onClick={() => handleItemClick(item)}
            key={item.order}
          >
            {item.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export const MobileNavigation = ({
  handleDrawerToggle,
  shouldShowNavLinks,
  siteNavigation,
  shouldShowUserMenu,
  shouldShowLoginLink,
  isMobileDrawerOpen,
}: MobileNavigationProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const drawerWidth = 240;

  const handleLogout = async () => {
    handleDrawerToggle();
    const response = await axios.get('/api/auth/logout');
    if (response.status === 200) {
      // OTT-1988 (PT-07): clear browser-side state before navigating, so no
      // user PII outlives the session. router.reload() is a full document load,
      // which re-runs the server branch and repopulates the site config.
      clearClientStorage();
      router.reload();
    }
  };

  return (
    <Box component="nav">
      <Drawer
        variant="temporary"
        open={isMobileDrawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { sm: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        <Box>
          <Box sx={{ p: 2, display: 'flex' }}>
            <Button
              onClick={handleDrawerToggle}
              startIcon={<Close />}
              sx={{ ml: 'auto', fontSize: '0.75rem' }}
            >
              {t('header.mobileMenuClose')}
            </Button>
          </Box>

          {shouldShowNavLinks && (
            <Box data-testid="navigation-links-mobile" pb={theme.spacing(2)}>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  href="/"
                  onClick={handleDrawerToggle}
                >
                  <ListItemText primary={t('header.homeLinkLabel')} />
                </ListItemButton>
              </ListItem>

              {siteNavigation?.map((item: INavigationItemConfiguration) =>
                item.children ? (
                  <SubmenuItemsMobile
                    navigation={item}
                    handleDrawerToggle={handleDrawerToggle}
                    key={item.order}
                  />
                ) : (
                  <ListItem key={item.name} disablePadding>
                    {item.type === 'ExternalLink' ? (
                      <ListItemButton
                        component="a"
                        href={item.urlSlug}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleDrawerToggle}
                      >
                        <ListItemText primary={item.name} />
                      </ListItemButton>
                    ) : (
                      <ListItemButton
                        component={Link}
                        href={`/${item.urlSlug}`}
                        onClick={handleDrawerToggle}
                      >
                        <ListItemText primary={item.name} />
                      </ListItemButton>
                    )}
                  </ListItem>
                )
              )}
            </Box>
          )}

          {shouldShowUserMenu && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  href="/account/edit-profile"
                  onClick={handleDrawerToggle}
                >
                  <ListItemText primary={t('header.sharedLinks.myAccount')} />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  href="/"
                  onClick={handleLogout}
                >
                  <ListItemText primary={t('header.sharedLinks.logout')} />
                </ListItemButton>
              </ListItem>
            </>
          )}

          {shouldShowLoginLink && (
            <ListItem disablePadding>
              <Button
                data-testid="login-button-mobile"
                LinkComponent={Link}
                variant="contained"
                href="/login"
                sx={{
                  height: '100%',
                  margin: theme.spacing(1),
                }}
              >
                {t('user:login.loginButton')}
              </Button>
            </ListItem>
          )}

          <Box>
            <LanguageSelectorMenu handleOptionClicked={handleDrawerToggle} />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

const NavigationItem = ({ item }: { item: INavigationItemConfiguration }) => {
  if (item.children) return <SubmenuItemsDesktop navigation={item} />;

  const isExternal = item.type === 'ExternalLink';
  const linkContent = <HeaderLinkDesktop> {item.name}</HeaderLinkDesktop>;

  if (isExternal) {
    return (
      <a href={item.urlSlug} target="_blank" rel="noopener noreferrer">
        {linkContent}
      </a>
    );
  }

  return <Link href={`/${item.urlSlug}`}>{linkContent}</Link>;
};

export const DesktopNavigation = ({
  siteNavigation,
}: DesktopNavigationProps) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: {
          xs: 'none',
          md: 'flex',
          marginRight: theme.spacing(2),
        },
        gap: theme.spacing(6),
      }}
      data-testid="navigation-links-desktop"
    >
      <Link href="/">
        <HeaderLinkDesktop>{t('header.homeLinkLabel')}</HeaderLinkDesktop>
      </Link>
      {siteNavigation?.map((item) => (
        <NavigationItem key={item.name} item={item} />
      ))}
    </Box>
  );
};
